import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getConditionById from 'editor/src/store/design/selector/getConditionById';
import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import getSpread from 'editor/src/store/design/selector/getSpread';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import {
  ElementAddress,
  MediaElement,
  MediaImage,
  MediaText,
  PrintTechnology,
  TextValueFormat,
} from 'editor/src/store/design/types';
import applyValueFormat, { isSameValueFormat } from 'editor/src/store/design/util/applyValueFormat';
import { receivesSharedValue } from 'editor/src/store/design/util/sharedTextFields';
import updateTextPropertiesWithoutRender from 'editor/src/store/design/util/updateTextPropertiesWithoutRender';
import isSampleFlagChangeAllowed from 'editor/src/store/editor/selector/isSampleFlagChangeAllowed';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';
import { RootState } from 'editor/src/store/index';
import { DPI_ADJUSTED } from 'editor/src/store/plugins/utils/imageManipulationUtils';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import calibrateReflowTextWidthOperation from './calibrateReflowTextWidthOperation';
import syncSharedTextValueOperation from './syncSharedTextValueOperation';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

export enum MediaUpdateActionName {
  UNKNOWN = 'Element changed',
  MOVED = 'Element moved',
  CROPPED = 'Element cropped',
  RESIZED = 'Element resized',
  ROTATED = 'Element rotated',
  TOGGLE_LOCK = 'Element locked/unlocked',
  TOGGLE_PERSO_LOCK = 'Element personalization locked/unlocked',
  TOGGLE_SAMPLE = 'Element toggle sample',
  TOGGLE_FOIL = 'Element toggle foil',
  TEXT_UPDATED = 'Text updated',
  VALUE_FORMAT_UPDATED = 'Text format updated',
  FONT_UPDATED = 'Text font updated',
  CHANGE_FOLDER_IMAGE = 'Image bundle changed',
  RENAMED = 'Element was renamed',
  COLOR_CHANGED = 'Color was changed',
  REMOVE_IMAGE_FROM_FRAME = 'Image removed from frame',
  ADJUSTMENT_APPLIED = 'An adjustment was applied',
  FILTER_APPLIED = 'A filter was applied',
  PATTERN_APPLIED = 'A pattern was applied',
  LINE_STROKE_UPDATED = 'Line Stroke updated',
  LINE_POSITION_UPDATED = 'Line position updated',
  RECT_UPDATED = 'Rect updated',
  LINE_EDGE_1_UPDATED = 'LINE_EDGE_1_UPDATED',
  LINE_EDGE_2_UPDATED = 'LINE_EDGE_2_UPDATED',
  LINE_ROUNDED_UPDATED = 'LINE_ROUNDED_UPDATED',
  LINE_DASH_PATTERN_UPDATED = 'LINE_DASH_PATTERN_UPDATED',
  RECTANGLE_POSITION_UPDATED = 'RECTANGLE_POSITION_UPDATED',
  RECTANGLE_STROKE_UPDATED = 'RECTANGLE_STROKE_UPDATED',
  RECTANGLE_FILL_UPDATED = 'RECTANGLE_FILL_UPDATED',
  MOCKUP_UPDATED = 'MOCKUP_UPDATED',
  TRANSFORM_PERSPECTIVE_UPDATED = 'TRANSFORM_PERSPECTIVE_UPDATED',
  PLACEHOLDER_UPDATE = 'PLACEHOLDER_UPDATE',
  DIGITIZING = 'DIGITIZING',
}

const updateMediaElementOperation =
  (
    elementAddress: ElementAddress,
    elementUpdate: Partial<MediaElement>,
    action: MediaUpdateActionName,
    preventSavingStore = false,
  ) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const element = getMediaElement(state, elementAddress);
    if (!element) {
      return;
    }

    // everything is the same, no need to update
    if ((Object.keys(elementUpdate) as Array<keyof MediaElement>).every((key) => element[key] === elementUpdate[key])) {
      return;
    }

    batch(() => {
      if (!preventSavingStore) {
        dispatch(saveUndoRedoStateOperation(action));
      }

      // The format this write leaves on the element. Read with `in`, not `??`,
      // and read once, because both the re-pour below and the group mirror at
      // the bottom have to agree with it: `updateMediaElementAction` assigns
      // `extra` wholesale, so an update whose extra omits the key CLEARS the
      // format — and falling back to the element's old one would re-pour the
      // value through a format on its way out, then tell the group to keep one
      // the source no longer has. Every caller spreads `...element.extra`
      // today; the same argument the sharedFieldId read below makes applies
      // here, which is to be precise before someone adds one.
      let nextValueFormat: TextValueFormat | undefined;

      // do we have a text style/content update
      if (element.type === 'text') {
        const textUpdate = elementUpdate as Partial<MediaText>;
        nextValueFormat =
          textUpdate.extra && 'valueFormat' in textUpdate.extra
            ? textUpdate.extra.valueFormat
            : element.extra.valueFormat;
        // Read like the format, so the write that binds a text already counts. A
        // bound text prints its code-set row raw, so neither the format nor a
        // picklist may rewrite it: the proof would show what the card does not.
        const nextVariableColumn =
          textUpdate.extra && 'variableColumn' in textUpdate.extra
            ? textUpdate.extra.variableColumn
            : element.extra.variableColumn;

        // Force the template's canonical form on whatever the buyer typed.
        // Normalising the stored value — rather than transforming at render —
        // means the print file inherits the formatting, since it is generated
        // from this same design data. Re-measure afterwards: callers size the
        // element from the raw string before dispatching, and horizontal
        // reflow packs by that width.
        //
        // VALUE_FORMAT_UPDATED is excluded on purpose: an author editing the
        // mask types it one character at a time, and re-pouring the value
        // through every half-written pattern destroys it. The mask governs
        // what the buyer types, so it takes effect on the next value edit.
        if (
          action === MediaUpdateActionName.TEXT_UPDATED &&
          textUpdate.extra?.text !== undefined &&
          !nextVariableColumn
        ) {
          const formatted = applyValueFormat(textUpdate.extra.text, nextValueFormat);
          if (formatted !== textUpdate.extra.text) {
            textUpdate.extra = { ...textUpdate.extra, text: formatted };
            updateTextPropertiesWithoutRender(element, textUpdate, undefined);
          }
        }

        // A text that varies by picklist option: an author edits it on the
        // canvas with the option active, and the edit is captured into THAT
        // option's content. This is what makes N labels x M languages
        // manageable — pick "Japanese" once, retype every label in place —
        // instead of a per-element table of M inputs. The live text and the
        // active option's variant are kept in lockstep so the swap on the next
        // option change starts from a consistent pair.
        //
        // Gated on the authoring host setting, because this same operation
        // carries the BUYER's typing in the personalizer (which never enables
        // it). Ungated, a text that is both personalisable and varying would
        // have the buyer's value overwrite the authored label for whichever
        // option happened to be active, and saved into their design.
        const variants = element.extra.textVariants;
        if (
          variants &&
          // Label mode has nothing to capture: the text IS the active option's
          // label, recomputed from the condition rather than typed by anyone.
          // Capturing the canvas edit into `values` here would both go unread
          // (the resolver ignores `values` in this mode) and silently revive a
          // per-option string the moment the author switches back to "own text".
          !variants.useOptionLabel &&
          !nextVariableColumn &&
          getHostSetting(state, 'enableTextConditionVariants') &&
          (action === MediaUpdateActionName.TEXT_UPDATED || action === MediaUpdateActionName.FONT_UPDATED) &&
          textUpdate.extra
        ) {
          const owningCondition = getConditionById(state, variants.conditionId)?.condition;
          if (owningCondition) {
            textUpdate.extra = {
              ...textUpdate.extra,
              textVariants: {
                ...variants,
                values: {
                  ...variants.values,
                  [owningCondition.activeOptionId]: {
                    text: textUpdate.extra.text ?? element.extra.text,
                    fontFamily: textUpdate.extra.fontFamily ?? element.extra.fontFamily,
                  },
                },
              },
            };
          }
        }

        // text was changed, it's not a sample anymore
        if (
          !isSampleFlagChangeAllowed(state) &&
          element.sample &&
          textUpdate.extra &&
          (!isEmbedded || element.extra.text !== textUpdate.extra.text)
        ) {
          textUpdate.sample = undefined;
        }

        // Clear stale SVG path when text changes on custom fonts — will be regenerated
        if (
          action === MediaUpdateActionName.TEXT_UPDATED &&
          element.extra.fontFamily?.startsWith('custom-') &&
          textUpdate.extra?.text !== undefined &&
          textUpdate.extra.text !== element.extra.text
        ) {
          textUpdate.path = undefined;
        }
      }

      if (action === MediaUpdateActionName.RESIZED && element.type === 'image') {
        const imageUpdate = elementUpdate as Partial<MediaImage>;
        imageUpdate.adjustments = imageUpdate.adjustments?.filter(({ name }) => name !== DPI_ADJUSTED);

        // Update embroidery scale for embroidery images during resize
        const spread = getSpread(state, state.editor.currentSpreadIndex);
        if (spread?.technology === PrintTechnology.Embroidery) {
          const imageElement = element as MediaImage;
          const currentScale = imageElement.embroideryScale || 1;

          // Calculate scale based on width change (could also use height or average)
          if (imageUpdate.pw && imageElement.pw) {
            const scaleFactor = imageUpdate.pw / imageElement.pw;
            imageUpdate.embroideryScale = Math.round(currentScale * scaleFactor * 100) / 100;
          }
        }
      }

      if (action === MediaUpdateActionName.TEXT_UPDATED && element.type === 'text') {
        dispatch(calibrateReflowTextWidthOperation(elementAddress, element, elementUpdate as Partial<MediaText>));
      }
      dispatch(updateMediaElementAction({ elementAddress, elementUpdate }));

      // If a text edit on a member of a reflow row may flip its row's
      // collapse state or change horizontal layout, recompute hidden + positions
      // in the same batch so the render sees a consistent state.
      if (
        (action === MediaUpdateActionName.TEXT_UPDATED ||
          action === MediaUpdateActionName.MOVED ||
          action === MediaUpdateActionName.FONT_UPDATED ||
          action === MediaUpdateActionName.RESIZED) &&
        element.type === 'text'
      ) {
        const spread = state.design.designData?.spreads[elementAddress.spreadIndex];
        const inReflowRow = spread?.reflowGroups?.some((g) =>
          g.rows.some((r) => r.elementUuids.includes(element.uuid)),
        );
        if (inReflowRow) {
          // The edited element's spread, not the viewer's: the membership check
          // right above reads elementAddress.spreadIndex, and per-option text
          // authoring addresses texts on spreads that are not on screen.
          dispatch(updateHiddenElementsOperation(elementAddress.spreadIndex));
        }
      }

      // A text shared across pages holds ONE value, so the string that just
      // landed has to reach the group's other members — this is the whole of
      // "typed once, printed on both sides". Read after the update above so it
      // carries the formatted value, not the raw keystroke.
      //
      // Deliberately not gated on the host setting that shows the authoring
      // control: the buyer's personalizer never enables it, and that is exactly
      // where the sharing has to work. The setting governs who may CREATE a
      // group, not whether an existing one holds together.
      // VALUE_FORMAT_UPDATED is in scope as well as TEXT_UPDATED, because the
      // mask editor is the one control that changes a format without changing
      // a value. Leaving it out let the author rewrite the front's mask and
      // have the back keep the old one — and the blur that follows cannot
      // recover it, since by then the element already holds the new mask and
      // the re-poured string often lands unchanged. From there the buyer's next
      // keystroke on the back stores the value under the STALE mask and carries
      // that format back onto the front, silently undoing the author's edit
      // into the print file.
      if (
        element.type === 'text' &&
        (action === MediaUpdateActionName.TEXT_UPDATED || action === MediaUpdateActionName.VALUE_FORMAT_UPDATED)
      ) {
        const textUpdate = elementUpdate as Partial<MediaText>;
        // `in`, not `??`: an update that clears the id while changing the text
        // is LEAVING the group, and `??` would read the absent key as "no
        // opinion" and fall through to the old id — propagating one last time
        // into the group it is on its way out of. No caller does that today
        // (the leave path writes the action directly), which is exactly why the
        // test should be the precise one before someone adds one.
        const sharedFieldId =
          textUpdate.extra && 'sharedFieldId' in textUpdate.extra
            ? textUpdate.extra.sharedFieldId
            : element.extra.sharedFieldId;
        const text = textUpdate.extra?.text;
        // Only when the string actually MOVED, the same test the custom-font
        // path above applies. Every option callback in the author's panel
        // (Required, hint, forced format, lock box size, flexible width)
        // dispatches TEXT_UPDATED spreading the element's own extra, so `text`
        // is defined on all of them. Propagating those would let a Required
        // toggle silently overwrite the rest of a group that had drifted apart
        // — an imported template, or a member that was locked while it did.
        const textMoved =
          action === MediaUpdateActionName.TEXT_UPDATED && text !== undefined && text !== element.extra.text;
        // The format is part of the field, not of the layer: the value is
        // STORED formatted, so a group whose members disagreed would keep
        // whichever format the buyer's page applied, and the other side would
        // print it. Compared by value, since the panel rebuilds the object on
        // every dispatch.
        const formatMoved = !isSameValueFormat(nextValueFormat, element.extra.valueFormat);
        // The source has to receive the group's value too, not just the
        // destinations. An author editing a member that keeps a string per
        // picklist option has that edit captured into the active option above
        // — pushing it onto the group as well would hand the author's
        // per-option string to every other member as the buyer's one value.
        if (sharedFieldId && receivesSharedValue(element) && (textMoved || formatMoved)) {
          // `text` only when it moved. A format-only change must not push this
          // element's string onto a group that has drifted apart — the same
          // rule the Required toggle is held to.
          dispatch(
            syncSharedTextValueOperation(element.uuid, sharedFieldId, {
              text: textMoved ? text : undefined,
              valueFormat: nextValueFormat,
            }),
          );
        }
      }
    });
  };

export default updateMediaElementOperation;
